import { useState, useEffect, useRef, useCallback } from "react";
import { FormikProps } from "formik";
import { PresetListModel, MediaModel, AllAdminPresets } from "@/database";
import { store } from "../../../store";
import { aiService } from "@/store/app/app.actions";
import { DenmarkPublish as DenmarkPublishInterface } from "@/database";
import type { MediaDetailsForm } from "../../../routes/pages/MediaDetail/MediaDetailsForm";
import {
  setAdminCurrentPresetDetails,
  setCurrentPresetDetails,
  setBorrowedMediaSocials,
  setBorrowedMediaPlaylists,
} from "@/store/media/media.slice";
import { AdminPreset } from "@/database";
import { urlUploadMediaThumbnail } from "@/store/media/media.action";
import { PRESET_MODES } from "@/utils/constants/uiConstants";
import { generateBaseUrl, getThumbnailUrlUtil } from "@/utils/mediaUtils";
import {
  TagKeyword,
  MANUAL_TAG_KEYWORD_DEFAULTS,
} from "@/components/ChipInput/TagsChipInput";

// `media.images` also carries other keys (a "base" placeholder with no real
// thumbnail, list of raw asset uploads, etc) that aren't real, applicable
// thumbnails - only these known slots are ever carried over. Kept in sync
// with THUMB_IMAGE_TYPES in CreateMetaPreset.tsx.
const THUMB_IMAGE_TYPES = ["thumb", "portrait", "social", "podcast", "poster"];

interface UsePresetManagementProps {
  presetList: PresetListModel[];
  getCurrentPresetDetails?: (params: { id: string }) => Promise<any>;
  getAdminCurrentPresetDetails?: (params: any) => Promise<any>;
  formik: FormikProps<MediaDetailsForm>;
  setCurrentSelectedSection: React.Dispatch<
    React.SetStateAction<DenmarkPublishInterface[]>
  >;
  currentMediaDetailsForEdit: MediaModel;
  onHasChanges?: (hasChanges: boolean) => void;
  adminPresetList?: AllAdminPresets;
}

interface UsePresetManagementReturn {
  // State
  presetId: string;
  isOpenPreviewPreset: boolean;
  mediaData: AdminPreset | null;
  selectedPresetDetails: AdminPreset | null;
  isNext: boolean;
  isApplied: boolean;
  openSelectPreset: boolean;

  // Handlers
  handleSelectPreset: (preset: any) => Promise<void>;
  handleNext: (value: boolean) => void;
  handlesetMediaData: (mediaData: any) => void;
  handleThumbToggle: (excludedThumbTypes: Set<string>) => void;
  handleSocialToggle: (excludedKeys: Set<string>) => void;
  handlePlaylistToggle: (excludedKeys: Set<string>) => void;
  handleNextClick: () => void;
  setOpenSelectPreset: (open: boolean) => void;
  setIsOpenPreviewPreset: (open: boolean) => void;
  closePreviewPreset: () => void;
}

/**
 * Custom hook to manage preset selection, preview, and application logic
 * Extracted from MediaDetailsForm to improve code organization and reusability
 */
export const usePresetManagement = ({
  presetList: _presetList,
  getCurrentPresetDetails: _getCurrentPresetDetails,
  getAdminCurrentPresetDetails,
  formik,
  setCurrentSelectedSection,
  currentMediaDetailsForEdit,
  onHasChanges,
  adminPresetList,
}: UsePresetManagementProps): UsePresetManagementReturn => {
  const [presetId, setPresetId] = useState<string>("");
  const [isOpenPreviewPreset, setIsOpenPreviewPreset] =
    useState<boolean>(false);
  const [mediaData, setMediaData] = useState<AdminPreset | null>(null);
  const [selectedPresetDetails, setSelectedPresetDetails] =
    useState<AdminPreset | null>(null);
  const [isNext, setIsNext] = useState<boolean>(false);
  const [isApplied, setIsApplied] = useState<boolean>(false);
  const [openSelectPreset, setOpenSelectPreset] = useState<boolean>(false);
  const mediaRef = useRef<any>(null);
  // Thumb types (e.g. "thumb", "portrait", "social", "podcast") unchecked from
  // the "Reuse Meta" thumbnails list - excluded from the borrowed-thumbnails
  // upload in handleNext. All thumb types are included by default.
  const excludedThumbTypesRef = useRef<Set<string>>(new Set());
  const handleThumbToggle = useCallback((excludedThumbTypes: Set<string>) => {
    excludedThumbTypesRef.current = excludedThumbTypes;
  }, []);
  // Social channels (keyed by `${channel_name}::${job_type}`) unchecked from
  // the "Reuse Meta" social chips list - excluded from the borrowed-socials
  // copy in handleNext.
  const excludedSocialKeysRef = useRef<Set<string>>(new Set());
  const handleSocialToggle = useCallback((excludedKeys: Set<string>) => {
    excludedSocialKeysRef.current = excludedKeys;
  }, []);
  // Playlists (keyed by playlist id) unchecked from the "Reuse Meta" playlist
  // chips list - excluded from the borrowed-playlists copy in handleNext.
  const excludedPlaylistKeysRef = useRef<Set<string>>(new Set());
  const handlePlaylistToggle = useCallback((excludedKeys: Set<string>) => {
    excludedPlaylistKeysRef.current = excludedKeys;
  }, []);

  /**
   * Handles AI translation preview for title and description
   */
  const handlePreviewAiTranslate = useCallback(
    async (
      field: string,
      text?: string,
      textToTranslate?: string,
      prefix?: string,
      suffix?: string
    ) => {
      try {
        // Notify listeners (e.g., TitleAndDesc) that AI translation has started
        window.dispatchEvent(
          new CustomEvent("metaPresetAiLoading", {
            detail: { field, loading: true },
          })
        );

        const safeTargetLanguage = text ?? "";
        const safeTextToTranslate = textToTranslate ?? "";

        const response = await store.dispatch(
          aiService({
            title: safeTextToTranslate,
            id: currentMediaDetailsForEdit.id,
            service: "prompt",
            target_language: safeTargetLanguage,
            type: field,
          })
        );

        if (response) {
          const safePrefix = prefix ?? "";
          const safeSuffix = suffix ?? "";
          const safeOutput = response.output_processed?.output ?? "";
          const finalText = `${safePrefix}${safeOutput}${safeSuffix}`;

          if (field === "title") {
            formik.setFieldValue("title", finalText);
          } else {
            formik.setFieldValue("desc", finalText);
          }
        }
      } catch (error) {
        console.error("Error in AI service:", error);
      } finally {
        // Notify listeners that AI translation has finished
        window.dispatchEvent(
          new CustomEvent("metaPresetAiLoading", {
            detail: { field, loading: false },
          })
        );
      }
    },
    [currentMediaDetailsForEdit.id, formik]
  );

  /**
   * Helper function to safely concatenate arrays
   */
  const concatArrays = useCallback(
    (newValue: any[] | undefined, existingValue: any[] | undefined) => {
      if (!newValue || !Array.isArray(newValue)) return existingValue;
      if (!existingValue || !Array.isArray(existingValue)) return newValue;
      return newValue.concat(existingValue);
    },
    []
  );

  /**
   * Helper function to safely concatenate strings
   */
  const concatStrings = useCallback(
    (newValue: string | undefined, existingValue: string | undefined) => {
      if (!newValue) return existingValue;
      if (!existingValue) return newValue;
      return existingValue + newValue;
    },
    []
  );

  /**
   * Applies preset data to formik form fields
   */
  const applyPresetData = useCallback(() => {
    if (!mediaRef.current) return;

    const preset = mediaRef.current as any;

    const normalizeMode = (mode?: string, defaultMode?: string) => {
      const normalized = mode?.toString().trim().toLowerCase();

      if (normalized === PRESET_MODES.FILL_IF_EMPTY) {
        return PRESET_MODES.FILL_IF_EMPTY;
      }
      if (normalized === PRESET_MODES.APPEND) {
        return PRESET_MODES.APPEND;
      }
      if (normalized === PRESET_MODES.OVERRIDE) {
        return PRESET_MODES.OVERRIDE;
      }

      // A media source (Reuse Meta -> select media) has no modes concept at
      // all - mapMediaToPresetPayload never sets any *_mode field - so every
      // field's mode always falls through to here. Only a real saved
      // preset's `defaultMode` (e.g. contributors/credits default to APPEND)
      // should apply; a media source must always override.
      if (isMediaSource) return PRESET_MODES.OVERRIDE;

      return (defaultMode || PRESET_MODES.OVERRIDE)
        .toString()
        .trim()
        .toLowerCase();
    };

    const isEmptyValue = (value: any) => {
      if (Array.isArray(value)) return value.length === 0;
      return value === undefined || value === null || value === "";
    };

    const applyScalarWithMode = (
      field: keyof MediaDetailsForm,
      newValue: unknown,
      mode?: string,
      defaultMode?: string
    ) => {
      if (newValue === undefined || newValue === null || newValue === "")
        return;

      const currentValue = (formik.values as Record<string, unknown>)[
        field as string
      ];
      const effectiveMode = normalizeMode(mode, defaultMode);

      if (effectiveMode === PRESET_MODES.FILL_IF_EMPTY) {
        if (!isEmptyValue(currentValue)) return;
        formik.setFieldValue(field as string, newValue);
        return;
      }

      // For scalar fields, treat APPEND the same as OVERRIDE
      formik.setFieldValue(field as string, newValue);
    };

    const applyArrayWithMode = (
      field: keyof MediaDetailsForm,
      newValue: unknown[],
      mode?: string,
      defaultMode?: string
    ) => {
      if (!Array.isArray(newValue) || newValue.length === 0) return;

      const currentValue = (formik.values as Record<string, unknown>)[
        field as string
      ];
      const effectiveMode = normalizeMode(mode, defaultMode);

      if (effectiveMode === PRESET_MODES.FILL_IF_EMPTY) {
        if (Array.isArray(currentValue) && currentValue.length > 0) return;
        formik.setFieldValue(field as string, newValue);
        return;
      }

      if (effectiveMode === PRESET_MODES.APPEND) {
        const existing = Array.isArray(currentValue) ? currentValue : [];
        const combined = concatArrays(newValue, existing);
        formik.setFieldValue(field as string, combined);
        return;
      }

      // OVERRIDE or fallback
      formik.setFieldValue(field as string, newValue);
    };

    // Set all available "simple" fields from preset object, excluding those
    // that are handled via mode-based logic or special handling below.
    const excludedBaseFields = new Set([
      "title",
      "desc",
      "tags",
      "team",
      "ctype",
      "lang",
      "category",
      "subcategory",
      "agency",
      "agency_credits",
      "mfk",
      "production_type",
      "producer",
      "editor",
      "anchor",
      "script_writer",
      "cameraman",
      "videocredit",
      "imagecredit",
      "audiocredit",
      "denmark",
    ]);

    Object.keys(preset).forEach((key) => {
      if (excludedBaseFields.has(key)) return;

      const value = preset[key];
      if (value === undefined || value === null || value === "") return;

      if (key in formik.values) {
        formik.setFieldValue(key, value);
      }
    });

    // Handle Denmark sections
    const denmarkSections = (preset?.denmark || [])
      .filter((item: any) => item.job_type !== "denmark_embed")
      .map((item: any) => ({
        ...item,
        job_type: "denmark", // Ensure job_type is set for chip rendering
      }));

    // Filter out sections from denmarkSections that are already present in currentMediaDetailsForEdit.socials with job_type 'denmark'
    const publishedDenmarkSections =
      currentMediaDetailsForEdit?.socials?.filter(
        (item: any) => item.job_type === "denmark"
      ) ?? [];
    const publishedSectionIds = new Set(
      publishedDenmarkSections.map((item: any) => item.section)
    );
    const filteredDenmarkSections = denmarkSections.filter(
      (item: any) => !publishedSectionIds.has(item.section)
    );

    // Determine parent/child hierarchy using the same rules as useDenmarkPublish,
    // not by array position — so preset application is correct regardless of
    // whether a published parent already exists.
    let sectionsToSet: DenmarkPublishInterface[] = [];

    if (filteredDenmarkSections.length > 0) {
      // Mirror the published-parent detection from useDenmarkPublish
      type SocialItem = {
        job_type: string;
        status: number;
        section: number;
        ct?: unknown;
      };

      const publishedDenmarkItems = (
        currentMediaDetailsForEdit?.socials || []
      ).filter((s: SocialItem) => s.job_type === "denmark" && s.status !== 100);

      const pickOldestByCt = (items: SocialItem[]) => {
        if (items.length === 0) return undefined;
        const ctNum = (x: SocialItem) => {
          const n = Number(x?.ct);
          return Number.isFinite(n) ? n : Infinity;
        };
        return items.reduce(
          (oldest, s) => (ctNum(s) < ctNum(oldest) ? s : oldest),
          items[0]
        );
      };

      const oldestPublishedParent = pickOldestByCt(publishedDenmarkItems);
      const publishedDenmarkCount = publishedDenmarkItems.length;

      // Draft parents already queued in currentSelectedSection (from a prior
      // manual selection in this same editing session)
      // Note: at preset-apply time currentSelectedSection hasn't been updated
      // yet, so there are no draft parents to count — but we check anyway for
      // correctness when presets are applied after manual selections.
      const existingDraftParents = ([] as DenmarkPublishInterface[]).filter(
        (item) => item.job_type === "denmark" && !item._parentSectionId
      );
      const totalParentCount =
        publishedDenmarkCount + existingDraftParents.length;

      /** Builds the backend-safe multi_publish_targets child shape */
      const toChildTarget = (child: DenmarkPublishInterface) => ({
        section: child.section,
        parent_id: child.section,
        channel_name: child.channel_name ?? "",
        host: child.host,
        folder_path: child.folder_path ?? "",
        open_story_at: "PUBLISH_SECTION" as const,
        open_newly_in_browser: false,
        isOverRideLinkRequired: true,
        contentPublishType: "MULTIPUBLISH" as const,
      });

      if (totalParentCount === 0) {
        // No existing parent → first preset section becomes the parent,
        // rest become its multi_publish_targets children.
        const [firstSection, ...rest] = filteredDenmarkSections;
        const childChips: DenmarkPublishInterface[] = rest.map(
          (child: DenmarkPublishInterface) => ({
            ...child,
            _parentSectionId: firstSection.section,
            contentPublishType: "MULTIPUBLISH" as const,
          })
        );
        const parentEntry: DenmarkPublishInterface = {
          ...firstSection,
          multi_publish_targets: [
            ...(firstSection.multi_publish_targets || []),
            ...rest.map(toChildTarget),
          ],
        };
        sectionsToSet = [parentEntry, ...childChips];
      } else {
        // One or more parents already exist → every preset section becomes a
        // child, attached to the oldest published parent (same rule as
        // useDenmarkPublish when totalParentCount >= 1).
        const parentSection = oldestPublishedParent ?? existingDraftParents[0];
        if (parentSection) {
          const childChips: DenmarkPublishInterface[] =
            filteredDenmarkSections.map((child: DenmarkPublishInterface) => ({
              ...child,
              _parentSectionId: parentSection.section,
              contentPublishType: "MULTIPUBLISH" as const,
            }));
          // Inject the new targets into the published parent so buildDenmarkPayload
          // picks them up correctly via the childrenOfPublished path.
          sectionsToSet = childChips;
        }
      }
    }
    setCurrentSelectedSection(sectionsToSet);

    // setCurrentSelectedSection(denmarkSections);

    // Media-sourced "Reuse Meta" has no prefix/suffix concept - title_suffix/
    // description_suffix simply carry the source media's own title/desc, so
    // they should override rather than be appended onto the current values.
    const isMediaSource = !!preset?._isMediaSource;

    // Build title with proper handling of undefined prefix/suffix
    const titlePrefix = preset?.title_prefix || "";
    const titleSuffix = preset?.title_suffix || "";
    const title = isMediaSource
      ? titleSuffix || formik.values.title
      : titlePrefix + formik.values.title + titleSuffix;

    // Build description with proper handling of undefined prefix/suffix
    const descPrefix = preset?.description_prefix || "";
    const descSuffix = preset?.description_suffix || "";
    const desc = isMediaSource
      ? descSuffix || formik.values.desc
      : descPrefix + formik.values.desc + descSuffix;

    // Handle special concatenation cases
    // Title & description: always apply using prefix/suffix (no mode support)

    // Mode-based application for remaining fields (except title/desc)
    const {
      tags_mode,
      meta_team_mode,
      content_type_mode,
      production_type_mode,
      language_mode,
      category_mode,
      sub_category_mode,
      agency_mode,
      agency_credits_mode,
      content_rating_mode,
      producers_mode,
      editors_mode,
      anchors_mode,
      script_writers_mode,
      cameramen_mode,
      photographers_mode,
      researchers_mode,
      compilers_mode,
      curators_mode,
      reporters_mode,
      authors_mode,
      image_credits_mode,
      video_credits_mode,
      audio_credits_mode,
    } = preset;

    // Simple scalar fields
    if (preset?.team !== undefined) {
      applyScalarWithMode(
        "team",
        preset.team,
        meta_team_mode,
        PRESET_MODES.OVERRIDE
      );
    }

    // Tags: use append/de-duplication logic when mode is APPEND
    if (preset?.tags) {
      const tagStr = (t: unknown): string =>
        typeof t === "string" ? t : String((t as { name?: string })?.name ?? t);

      const currentTags = Array.isArray(formik.values.tags)
        ? formik.values.tags
        : [];
      const presetTags = Array.isArray(preset.tags) ? preset.tags : [];

      const currentDenmarkKeywords: TagKeyword[] = Array.isArray(
        formik.values.denmark_keywords
      )
        ? (formik.values.denmark_keywords as unknown as TagKeyword[])
        : [];
      const existingKeywordsByName = new Map(
        currentDenmarkKeywords.map((k) => [k.keywordName, k])
      );
      const buildDenmarkKeywords = (finalTags: unknown[]): TagKeyword[] =>
        finalTags.map((t) => {
          const name = tagStr(t);
          return (
            existingKeywordsByName.get(name) ?? {
              ...MANUAL_TAG_KEYWORD_DEFAULTS,
              keywordName: name,
            }
          );
        });

      if (presetTags.length) {
        const effectiveMode = normalizeMode(tags_mode, PRESET_MODES.APPEND);

        if (effectiveMode === PRESET_MODES.FILL_IF_EMPTY) {
          if (
            Array.isArray(currentTags) &&
            currentTags.length > 0 &&
            !isEmptyValue(currentTags)
          ) {
            // Keep existing tags when mode is fill-if-empty and tags already exist
          } else {
            formik.setFieldValue("tags", presetTags);
            formik.setFieldValue(
              "denmark_keywords",
              buildDenmarkKeywords(presetTags),
              true
            );
          }
        } else if (effectiveMode === PRESET_MODES.APPEND) {
          const formikTagSet = new Set(currentTags.map((t) => tagStr(t)));
          const tagsToAppend = presetTags.filter((t) => {
            const val = tagStr(t);
            return val != null && val !== "" && !formikTagSet.has(val);
          });
          const mergedTags = [...currentTags, ...tagsToAppend];
          formik.setFieldValue("tags", mergedTags);
          formik.setFieldValue(
            "denmark_keywords",
            buildDenmarkKeywords(mergedTags),
            true
          );
        } else {
          // OVERRIDE: preset replaces the tag list. Keep a current tag only when it
          // is not exactly in the preset but matches some preset tag case-insensitively
          // (so "test" stays alongside preset "Test"; unrelated tags are still dropped).
          const presetTagStrs = presetTags
            .map((t) => tagStr(t))
            .filter((s) => s !== "");
          const presetTagKeys = new Set(presetTagStrs);
          const presetTagKeysLower = new Set(
            presetTagStrs.map((s) => s.toLowerCase())
          );
          const retained = currentTags.filter((t) => {
            const s = tagStr(t);
            if (s === "") return false;
            if (presetTagKeys.has(s)) return false;
            return presetTagKeysLower.has(s.toLowerCase());
          });
          const finalTags = [...presetTags, ...retained];
          formik.setFieldValue("tags", finalTags);
          formik.setFieldValue(
            "denmark_keywords",
            buildDenmarkKeywords(finalTags),
            true
          );
        }
      }
    }
    if (preset?.ctype !== undefined) {
      applyScalarWithMode(
        "ctype",
        preset.ctype,
        content_type_mode,
        PRESET_MODES.OVERRIDE
      );
    }
    if (preset?.production_type !== undefined) {
      applyScalarWithMode(
        "production_type",
        preset.production_type,
        production_type_mode,
        PRESET_MODES.OVERRIDE
      );
    }
    if (preset?.lang !== undefined) {
      applyScalarWithMode(
        "lang",
        preset.lang,
        language_mode,
        PRESET_MODES.OVERRIDE
      );
    }
    if (preset?.category !== undefined) {
      applyScalarWithMode(
        "category",
        preset.category,
        category_mode,
        PRESET_MODES.OVERRIDE
      );
    }
    if (preset?.subcategory !== undefined) {
      applyScalarWithMode(
        "subcategory",
        preset.subcategory,
        sub_category_mode,
        PRESET_MODES.OVERRIDE
      );
    }
    if (preset?.agency !== undefined) {
      applyScalarWithMode(
        "agency",
        preset.agency,
        agency_mode,
        PRESET_MODES.OVERRIDE
      );
    }
    if (preset?.mfk !== undefined) {
      applyScalarWithMode(
        "mfk",
        preset.mfk,
        content_rating_mode,
        PRESET_MODES.OVERRIDE
      );
    }

    // Array fields default to APPEND when mode is not provided
    if (preset?.agency_credits) {
      applyArrayWithMode(
        "agency_credits",
        preset.agency_credits,
        agency_credits_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.producer) {
      applyArrayWithMode(
        "producer",
        preset.producer,
        producers_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.editor) {
      applyArrayWithMode(
        "editor",
        preset.editor,
        editors_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.anchor) {
      applyArrayWithMode(
        "anchor",
        preset.anchor,
        anchors_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.script_writer) {
      applyArrayWithMode(
        "script_writer",
        preset.script_writer,
        script_writers_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.cameraman) {
      applyArrayWithMode(
        "cameraman",
        preset.cameraman,
        cameramen_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.photographer) {
      applyArrayWithMode(
        "photographer",
        preset.photographer,
        photographers_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.researcher) {
      applyArrayWithMode(
        "researcher",
        preset.researcher,
        researchers_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.compiler) {
      applyArrayWithMode(
        "compiler",
        preset.compiler,
        compilers_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.curator) {
      applyArrayWithMode(
        "curator",
        preset.curator,
        curators_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.reporter) {
      applyArrayWithMode(
        "reporter",
        preset.reporter,
        reporters_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.author) {
      applyArrayWithMode(
        "author",
        preset.author,
        authors_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.imagecredit) {
      applyArrayWithMode(
        "imagecredit",
        preset.imagecredit,
        image_credits_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.videocredit) {
      applyArrayWithMode(
        "videocredit",
        preset.videocredit,
        video_credits_mode,
        PRESET_MODES.APPEND
      );
    }
    if (preset?.audiocredit) {
      applyArrayWithMode(
        "audiocredit",
        preset.audiocredit,
        audio_credits_mode,
        PRESET_MODES.APPEND
      );
    }

    // Handle AI translation previews
    const isTitleCustomPrompt =
      preset?.title_ai_type?.toString().trim().toLowerCase() ===
      "custom prompt";
    const isDescCustomPrompt =
      preset?.desc_ai_type?.toString().trim().toLowerCase() === "custom prompt";

    if (preset?.desc_ai_type?.length > 0 && preset?.desc_ai_type !== "None") {
      handlePreviewAiTranslate(
        "description",
        preset?.desc_ai_type === "Custom Prompt"
          ? preset?.desc_ai_value
          : preset?.desc_ai_type === "None"
            ? ""
            : preset?.desc_ai_type,
        isDescCustomPrompt ? desc : formik.values.desc,
        isDescCustomPrompt ? "" : descPrefix,
        isDescCustomPrompt ? "" : descSuffix
      );
    } else {
      formik.setFieldValue("desc", desc);
    }
    if (preset?.title_ai_type?.length > 0 && preset?.title_ai_type !== "None") {
      handlePreviewAiTranslate(
        "title",
        preset?.title_ai_type === "Custom Prompt"
          ? preset?.title_ai_value
          : preset?.title_ai_type === "None"
            ? ""
            : preset?.title_ai_type,
        isTitleCustomPrompt ? title : formik.values.title,
        isTitleCustomPrompt ? "" : titlePrefix,
        isTitleCustomPrompt ? "" : titleSuffix
      );
    } else {
      formik.setFieldValue("title", title);
    }

    // Notify parent component about changes
    if (onHasChanges) {
      onHasChanges(true);
    }
  }, [
    formik,
    setCurrentSelectedSection,
    concatArrays,
    concatStrings,
    handlePreviewAiTranslate,
    onHasChanges,
  ]);

  /**
   * Effect: When presetList or presetId changes, find and set media data
   */
  // useEffect(() => {
  //   if (presetList.length > 0 && presetId !== "") {
  //     const preset = presetList.find((preset: any) => preset.id === presetId);
  //     const mediaInfo = preset?.media?.[0];

  //     if (mediaInfo) {
  //       setMediaData(mediaInfo as MediaModel);
  //       setIsOpenPreviewPreset(true);
  //     }
  //   }
  // }, [presetList, presetId]);

  /**
   * Effect: When isNext is true, apply preset data to form
   */
  useEffect(() => {
    if (isNext === false) return;

    if (
      presetId !== "" &&
      adminPresetList?.meta_preset &&
      adminPresetList?.meta_preset?.length > 0
    ) {
      applyPresetData();
    }

    // Cleanup after applying
    setMediaData(null);
    setSelectedPresetDetails(null);
    mediaRef.current = null;
    setIsNext(false);
    setPresetId("");
  }, [presetId, isNext, applyPresetData]);

  /**
   * Effect: When selectedPresetDetails changes, open preview modal
   */
  useEffect(() => {
    if (
      selectedPresetDetails &&
      selectedPresetDetails.id &&
      Object.keys(selectedPresetDetails).length > 0
    ) {
      setIsOpenPreviewPreset(true);
      setOpenSelectPreset(false);
    }
  }, [selectedPresetDetails]);

  /**
   * Handles preset selection from the preset list
   */
  const handleSelectPreset = useCallback(
    async (preset: any) => {
      setPresetId(preset.id);
      // Reset previous state first
      setSelectedPresetDetails(null);
      setMediaData(null);
      excludedThumbTypesRef.current = new Set();
      excludedSocialKeysRef.current = new Set();
      excludedPlaylistKeysRef.current = new Set();

      store.dispatch(
        setCurrentPresetDetails({
          data: { list: preset },
          error: null,
          isLoading: false,
        })
      );

      store.dispatch(
        setAdminCurrentPresetDetails({
          data: { list: preset },
          error: null,
          isLoading: true,
        })
      );

      setMediaData(preset);
      setSelectedPresetDetails(preset);

      try {
        if (getAdminCurrentPresetDetails) {
          const response = await getAdminCurrentPresetDetails({
            id: preset.id,
            type: "meta_preset",
          });

          if (response && response.status === 200 && response.data) {
            setMediaData(response.data);
            setSelectedPresetDetails(response.data);
            // Update Redux store with complete data
            store.dispatch(
              setAdminCurrentPresetDetails({
                data: response.data,
                error: null,
                isLoading: false,
              })
            );
          }
        }
      } catch (error) {
        console.error("Error fetching preset details:", error);
        store.dispatch(
          setCurrentPresetDetails({
            data: undefined,
            error: "Failed to fetch preset details",
            isLoading: false,
          })
        );
      }
    },
    [getAdminCurrentPresetDetails]
  );

  /**
   * Handles the next/apply action from preview modal
   */
  const handleNext = useCallback(
    (value: boolean) => {
      // Only now (the user actually clicked Apply) commit socials borrowed
      // from a "Reuse Meta" -> select media pick, so the Social Publish page
      // pre-opens them. Closing/cancelling the preview must not affect the
      // media being edited. Dispatched here (a plain event-handler callback,
      // not inside a render-triggered effect) to avoid interleaving with the
      // formik update cascade that applyPresetData() causes.
      if (value) {
        const allAppliedSocials =
          (selectedPresetDetails as any)?.socials ||
          (mediaData as any)?.socials ||
          [];
        const appliedSocials = allAppliedSocials.filter(
          (social: any) =>
            !excludedSocialKeysRef.current.has(
              `${social?.channel_name}::${social?.job_type}`
            )
        );
        if (appliedSocials.length > 0) {
          const sourceMediaId =
            selectedPresetDetails?.id || mediaData?.id || "";
          store.dispatch(
            setBorrowedMediaSocials({ sourceMediaId, socials: appliedSocials })
          );
        }

        // Same "only commit on Apply" treatment for playlists borrowed from
        // a "Reuse Meta" -> select media pick, so the Playlist section shows
        // them merged in as newly-added chips.
        const allAppliedPlaylists =
          (selectedPresetDetails as any)?.playlists ||
          (mediaData as any)?.playlists ||
          [];
        const appliedPlaylists = allAppliedPlaylists.filter(
          (playlist: any) => !excludedPlaylistKeysRef.current.has(playlist?.id)
        );
        if (appliedPlaylists.length > 0) {
          const sourceMediaId =
            selectedPresetDetails?.id || mediaData?.id || "";
          const allPlaylistsData =
            (selectedPresetDetails as any)?.playlistsData ||
            (mediaData as any)?.playlistsData ||
            [];
          store.dispatch(
            setBorrowedMediaPlaylists({
              sourceMediaId,
              playlists: appliedPlaylists,
              playlistsData: allPlaylistsData,
            })
          );
        }

        // "Reuse Meta" -> select media only copies metadata fields (see
        // mapMediaToPresetPayload) - it doesn't carry the source media's
        // actual thumbnail images over. If the source has any (thumb,
        // portrait, social, podcast, or any other type present on its
        // `images`), ask the backend to fetch each from the source's own
        // thumbnail URL and apply it onto the media being edited
        // (currentMediaDetailsForEdit), one assets.urlimageupload call per
        // type, each carrying its own thumb_type.
        const sourceMedia: any = selectedPresetDetails?._isMediaSource
          ? selectedPresetDetails
          : (mediaData as any)?._isMediaSource
            ? mediaData
            : null;

        const sourceImages = sourceMedia?.images;
        if (sourceImages && typeof sourceImages === "object") {
          THUMB_IMAGE_TYPES.filter(
            (thumbType) =>
              typeof sourceImages[thumbType] === "string" &&
              !excludedThumbTypesRef.current.has(thumbType)
          ).forEach((thumbType) => {
            const sourceThumbId = sourceImages[thumbType];
            const sourceThumbUrl = getThumbnailUrlUtil({
              media: sourceMedia,
              id: sourceThumbId,
              source: sourceMedia?.source,
              baseUrl: generateBaseUrl(sourceMedia),
            });
            if (!sourceThumbUrl) return;

            store.dispatch(
              urlUploadMediaThumbnail({
                fid: `${currentMediaDetailsForEdit.damid}${sourceThumbId}`,
                title: currentMediaDetailsForEdit.title,
                source: currentMediaDetailsForEdit.source,
                name: "thumb",
                type: "thumb",
                isShorts: !!currentMediaDetailsForEdit.is_short,
                is_short: currentMediaDetailsForEdit.is_short,
                id: currentMediaDetailsForEdit.id,
                cdn: currentMediaDetailsForEdit.cdn,
                ctype: currentMediaDetailsForEdit.ctype,
                damid: currentMediaDetailsForEdit.damid,
                stitle: currentMediaDetailsForEdit.stitle,
                desc: currentMediaDetailsForEdit.desc,
                default: false,
                business: currentMediaDetailsForEdit.business,
                downloads: {
                  image: {
                    url: sourceThumbUrl,
                    thumb_type: thumbType,
                  },
                },
              }) as any
            );
          });
        }
      }

      setIsNext(value);
      setIsApplied(true);
      setIsOpenPreviewPreset(false);
    },
    [selectedPresetDetails, mediaData, currentMediaDetailsForEdit]
  );

  /**
   * Sets media data in ref for later application
   */
  const handlesetMediaData = useCallback((mediaData: any) => {
    mediaRef.current = mediaData;
  }, []);

  /**
   * Handles next click event - dispatches custom event and resets media data
   */
  const handleNextClick = useCallback(() => {
    const event = new CustomEvent("previewNextClickMetaPreset");
    window.dispatchEvent(event);
    setMediaData(null);
  }, []);

  /**
   * Closes preview preset modal and resets related state
   */
  const closePreviewPreset = useCallback(() => {
    setIsOpenPreviewPreset(false);
    setPresetId("");
    setIsNext(false);
    setMediaData(null);
  }, []);

  return {
    // State
    presetId,
    isOpenPreviewPreset,
    mediaData,
    selectedPresetDetails,
    isNext,
    isApplied,
    openSelectPreset,

    // Handlers
    handleSelectPreset,
    handleNext,
    handlesetMediaData,
    handleThumbToggle,
    handleSocialToggle,
    handlePlaylistToggle,
    handleNextClick,
    setOpenSelectPreset,
    setIsOpenPreviewPreset,
    closePreviewPreset,
  };
};
